import {
  useHttpClient,
  useNetworkStatus,
  useReducerStorage as useReducer,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { ProposalOptionNameDefault } from './proposalOptionNameDefaults.models';
import {
  initialState,
  proposalOptionNameDefaultsReducer,
} from './proposalOptionNameDefaults.reducer';

export const suggestedOptionNameDefaults = ['Platinum', 'Gold', 'Silver'];
const defaultAlternatives = [
  'Optimum',
  'Premium',
  'Deluxe',
  'Basic',
  'Platinum',
  'Gold',
  'Silver',
  'Bronze',
];

export const getOptionNameDefaults = (customNames?: string[]): string[] => [
  ...new Set([...(customNames || []), ...defaultAlternatives]),
];

interface ProposalOptionNameDefaultsContextProps {
  optionNames: ProposalOptionNameDefault[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchOptionNameData: (organizationId: string) => void;
}

const ProposalOptionNameDefaultsContext = createContext<
  ProposalOptionNameDefaultsContextProps | undefined
>(undefined);

export const ProposalOptionNameDefaultsProvider: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const [state, dispatch] = useReducer(
    proposalOptionNameDefaultsReducer,
    initialState,
    {
      key: 'airship-cache:option-name-defaults',
      select: (s) => ({ ids: s.ids, entities: s.entities }),
    }
  );
  const isOnline = useNetworkStatus();
  const { showToast } = useToast();
  const httpClient = useHttpClient();

  useEffect(() => {
    state.error &&
      showToast(
        `Error fetching organization option names: ${state.error}`,
        'error'
      );
  }, [showToast, state.error]);

  const optionNames = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.entities, state.ids]
  );

  const fetchOptionNameData = useCallback(
    async (organizationId: string) => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }

      dispatch({ type: 'FETCH_OPTION_NAMES' });

      try {
        const optionNames = await httpClient.get<ProposalOptionNameDefault[]>(
          `/organizations/${organizationId}/option-names`
        );

        dispatch({ type: 'FETCH_OPTION_NAMES_SUCCESS', payload: optionNames });
      } catch (error) {
        dispatch({
          type: 'FETCH_OPTION_NAMES_FAILURE',
          payload: { error: `${error}` },
        });
      }
    },
    [httpClient, isOnline]
  );

  return (
    <ProposalOptionNameDefaultsContext.Provider
      value={{
        fetchOptionNameData,
        optionNames,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
      }}
    >
      {children}
    </ProposalOptionNameDefaultsContext.Provider>
  );
};

export const useProposalOptionNameDefaults = () => {
  const context = useContext(ProposalOptionNameDefaultsContext);
  if (!context) {
    throw new Error(
      'useProposalOptionNameDefaults must be used within a ProposalOptionNameDefaultsProvider'
    );
  }
  return context;
};
